import { API_MAX_CATEGORIES_LIMIT } from 'common/config/constants';
import variables from 'common/config/variables';
import {
  CATEGORIES_GET,
  RECORDED_CLEAR_CATEGORIES_EPGS,
  RECORDED_CLEAR_CATEGORY_LIST,
  RECORDED_CLEAR_SUBCATEGORIES_EPGS,
  RECORDED_CLEAR_SUBCATEGORY_LIST,
  RECORDED_GET_CATEGORY_EPGS,
  RECORDED_GET_CATEGORY_LIST_NEXT_PAGE,
  RECORDED_GET_SUBCATEGORY_EPGS,
  RECORDED_GET_SUBCATEGORY_LIST_NEXT_PAGE,
  RECORDED_RESET,
  SET_CATEGORIES_OPTIONS
} from 'common/constants/action-types';
import { partitionArray } from 'common/services/helpers';
import request from 'common/services/request';
import { createContinueWatchingPropagator } from 'common/utils/last-watched';
import { propagateChannelAccessToEpg } from 'common/utils/profile-settings';
import { isEmpty } from 'lodash';
import { store } from '../..';

const requestFilteredEpgs = async (url, data, vod = false) => {
  const response = await request({
    method: 'post',
    url: `epgs${vod ? '/vod' : ''}${url}`,
    data
  });
  const { profileSettings, liveChannels, lastWatched } = store.getState();
  const propagateContinueWatching = createContinueWatchingPropagator(lastWatched);
  const entries = Object.entries(response.data).map(([categoryId, epgs]) => [
    categoryId,
    // filter all adult channels
    epgs
      .filter((epg) => !liveChannels[epg.chanId]?.adult)
      .map((epg) =>
      ({
        ...propagateChannelAccessToEpg(epg, profileSettings),
        ...propagateContinueWatching(epg)
      }))
  ]);
  return {
    ...response,
    data: Object.fromEntries(entries)
  };
};

const setCategoriesOptions = (categories, vod = false) => {
  return async (dispatch) => {
    const options = {}
    Object.values(categories).forEach((category) => {
      const categoryOption = variables.VOD_CATEGORY_OPTIONS[category.id]
      const option = isEmpty(categoryOption) ? variables.DEFAULT_VOD_CATEGORY_OPTIONS : categoryOption
      options[category.id] = { ...option }
    })
    dispatch({
      type: SET_CATEGORIES_OPTIONS,
      payload: { options, vod }
    });
  }
}

export const getRecordedCategories = (vod) => {

  return async (dispatch) => {
    try {
      const ajax = request({
        method: 'get',
        url: `/epgs${vod ? '/vod' : ''}/categories${vod ? '/all' : ''}`,
      });
      const { data } = await ajax;
      dispatch(setCategoriesOptions(data, vod));
      dispatch({
        type: CATEGORIES_GET,
        payload: { data, vod }
      });
      return data;
    } catch (error) {
      console.warn(error)

    }
  };
};

// FOR THE PAGE WITH ALL CATEGORIES:

const createInitialCategoriesQuery = (idList, count = 20) => {
  return { categories: idList.map((id) => ({ count, id, offset: 0 })) };
};

export const getListsForManyCategories = (vod) => {
  return async (dispatch, getState) => {
    try {
      let { epgs } = getState();
      const recorded = epgs[vod ? 'vod' : 'recorded'];
      if (isEmpty(recorded)) {
        recorded = { categories: await getRecordedCategories() };
      }
      const data = createInitialCategoriesQuery(Object.keys(recorded.categories));
      const promises = partitionArray(data.categories, API_MAX_CATEGORIES_LIMIT).map(
        (categories) => {
          return requestFilteredEpgs('/categories/many', { categories }, vod);
        }
      );
      const res = await Promise.all(promises);
      const accumulatedData = res.reduce((accumulated, current) => {
        return {
          ...accumulated,
          ...current.data
        };
      }, {});

      dispatch({
        type: RECORDED_GET_CATEGORY_EPGS,
        payload: { data: accumulatedData, vod }
      });
    } catch (error) {
      console.warn(error)
    }
  };
};

export const getNextPageForCategory = (queryData, vod) => {
  return async (dispatch, getState) => {
    try {
      const ajax = requestFilteredEpgs('/categories/many', queryData, vod);

      const catId = queryData.categories[0].id;
      const { data } = await ajax;
      const prevState = getState().epgs[vod ? 'vod' : 'recorded'].categoriesEpgs[catId];
      dispatch({
        type: RECORDED_GET_CATEGORY_EPGS,
        payload: { data: data[catId].length > 0 ? data : prevState, vod }
      });

    } catch (error) {
      console.warn(error)
    }
  };
};

export const getPrevPageForCategory = (queryData, vod) => {
  return async (dispatch) => {
    try {
      const ajax = requestFilteredEpgs('/categories/many', queryData, vod);

      const { data } = await ajax;
      dispatch({
        type: RECORDED_GET_CATEGORY_EPGS,
        payload: { data, vod }
      });
    } catch (error) {
      console.warn(error)
    }
  };
};

export const clearCategoriesEpgs = (vod) => {
  return async (dispatch) => {
    dispatch({
      type: RECORDED_CLEAR_CATEGORIES_EPGS,
      payload: { vod }
    });
  };
};

// FOR THE PAGE WITH ALL SUBCATEGORIES FOR A CATEGORY:

const createInitialSubcategoriesQuery = (idList, count = 20) => {
  return { subcategories: idList.map((id) => ({ count, id, offset: 0 })) };
};

export const getListsForManySubcategories = (category, vod) => {
  return async (dispatch) => {
    try {
      dispatch(clearSubcategoriesEpgs(vod));
      const subcategoriesIds = category.subcategories.map(({ id }) => id);
      const data = createInitialSubcategoriesQuery(subcategoriesIds);

      const promises = partitionArray(data.subcategories, API_MAX_CATEGORIES_LIMIT).map(
        (subcategories) => {
          return requestFilteredEpgs('/subcategories/many', { subcategories }, vod);
        }
      );
      const res = await Promise.all(promises);
      const accumulatedData = res.reduce((accumulated, current) => {
        return {
          ...accumulated,
          ...current.data
        };
      }, {});
      dispatch({
        type: RECORDED_GET_SUBCATEGORY_EPGS,
        payload: { data: accumulatedData, vod }
      });
      return accumulatedData;
    } catch (error) {
      console.warn(error);
    }
  };
};

export const getNextPageForSubcategory = (queryData, vod) => {
  return async (dispatch, getState) => {
    try {
      const ajax = requestFilteredEpgs('/subcategories/many', queryData, vod);

      const catId = queryData.subcategories[0].id;
      const { data } = await ajax;
      const prevState = getState().epgs[vod ? 'vod' : 'recorded'].subcategoriesEpgs[catId];
      dispatch({
        type: RECORDED_GET_SUBCATEGORY_EPGS,
        payload: { data: data[catId].length > 0 ? data : prevState, vod }
      });
    } catch (error) {
      console.warn(error)
    }
  };
};

export const getPrevPageForSubcategory = (queryData, vod) => {
  return async (dispatch) => {
    try {
      const ajax = requestFilteredEpgs('/subcategories/many', queryData, vod);

      const { data } = await ajax;
      dispatch({
        type: RECORDED_GET_SUBCATEGORY_EPGS,
        payload: { data, vod }
      });
    } catch (error) {
      console.warn(error)
    }
  };
};

export const clearSubcategoriesEpgs = (vod) => {
  return async (dispatch) => {
    dispatch({
      type: RECORDED_CLEAR_SUBCATEGORIES_EPGS,
      payload: { vod }
    });
  };
};

// FOR THE PAGE WITH LISTING FOR A CATEGORY:

export const getCategoryListNextPage = (id, offset, count, vod) => {
  return async (dispatch) => {
    try {
      const query = {
        categories: [{ id, offset, count }]
      };
      const ajax = requestFilteredEpgs('/categories/many', query, vod);

      const { data } = await ajax;

      dispatch({
        type: RECORDED_GET_CATEGORY_LIST_NEXT_PAGE,
        payload: { data, id, vod }
      });
    } catch (error) {
      console.warn(error)
    }
  };
};

export const clearCategoryList = (id, vod) => {
  return (dispatch) => {
    dispatch({
      type: RECORDED_CLEAR_CATEGORY_LIST,
      payload: { id, vod }
    });
  };
};

// FOR THE PAGE WITH LISTING FOR A SUBCATEGORY:

export const getSubcategoryListNextPage = (id, count = variables.DEFAULT_PAGE_SIZE, offset = 0, vod) => {
  return async (dispatch) => {
    try {
      const queryData = {
        subcategories: [{ id, offset, count }]
      };
      const ajax = requestFilteredEpgs('/subcategories/many', queryData, vod);
      const subCatId = queryData.subcategories[0].id;
      const { data } = await ajax;

      dispatch({
        type: RECORDED_GET_SUBCATEGORY_LIST_NEXT_PAGE,
        payload: { data, id: subCatId, vod }
      });
    } catch (error) {
      console.warn(error)
    }
  };
};

export const clearSubcategoryList = (id, vod) => {
  return (dispatch) => {
    dispatch({
      type: RECORDED_CLEAR_SUBCATEGORY_LIST,
      payload: { id, vod }
    });
  };
};

export const updateSubcategoryEpgs = (vod) => {
  return (dispatch, getState) => {
    try {
      const { profileSettings, epgs } = getState();
      const recorded = epgs[vod ? 'vod' : 'recorded'];
      const epgsWithAccessRights = Object.entries(recorded.subcategoriesEpgs).map(
        ([subCatId, epgs]) =>
          [subCatId, epgs.map((epg) => propagateChannelAccessToEpg(epg, profileSettings))]
      );
      dispatch({
        type: RECORDED_GET_SUBCATEGORY_EPGS,
        payload: { data: Object.fromEntries(epgsWithAccessRights), vod }
      });
    } catch (error) {
      console.warn(error)
    }
  };

}

export const resetRecorded = () => {
  return (dispatch) => {
    dispatch({
      type: RECORDED_RESET
    });
  };
};
